import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as derivedFieldsApi from '../api/derivedFields';

export function DerivedFields() {
  const { profileId } = useParams();
  const { selectedOrg } = useOrg();
  const { isAdmin, canReadAssets } = usePermissions();
  const [fields, setFields] = useState([]);
  const [allowedFunctions, setAllowedFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [form, setForm] = useState({
    key: '',
    dataset: 'sessions',
    fieldType: 'number',
    expression: '',
    description: ''
  });

  const loadFields = useCallback(async () => {
    if (!selectedOrg || !profileId || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await derivedFieldsApi.getDerivedFields(selectedOrg.id, profileId);
      setFields(data.derivedFields);
      setAllowedFunctions(data.allowedFunctions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, profileId, canReadAssets]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const handleOpenCreate = () => {
    setEditingField(null);
    setForm({
      key: '',
      dataset: 'sessions',
      fieldType: 'number',
      expression: '',
      description: ''
    });
    setValidationResult(null);
    setShowModal(true);
  };

  const handleOpenEdit = (field) => {
    setEditingField(field);
    setForm({
      key: field.key,
      dataset: field.dataset,
      fieldType: field.fieldType,
      expression: field.expression,
      description: field.description || ''
    });
    setValidationResult(null);
    setShowModal(true);
  };

  const handleValidate = async () => {
    if (!selectedOrg || !form.expression) return;
    try {
      const result = await derivedFieldsApi.validateExpression(selectedOrg.id, profileId, form.expression);
      setValidationResult(result);
    } catch (err) {
      setValidationResult({ valid: false, errors: [err.message] });
    }
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (editingField) {
        await derivedFieldsApi.updateDerivedField(selectedOrg.id, profileId, editingField.id, {
          key: form.key,
          dataset: form.dataset,
          fieldType: form.fieldType,
          expression: form.expression,
          description: form.description
        });
      } else {
        await derivedFieldsApi.createDerivedField(selectedOrg.id, profileId, form);
      }
      setShowModal(false);
      loadFields();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (field) => {
    if (!selectedOrg || !window.confirm('Delete this derived field?')) return;
    try {
      await derivedFieldsApi.deleteDerivedField(selectedOrg.id, profileId, field.id);
      loadFields();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view derived fields.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Derived Fields"
        subtitle={`Expression-based calculated fields for profile ${profileId}`}
        breadcrumbs={[
          { label: 'Report Profiles', href: '/admin/report-profiles' },
          { label: 'Derived Fields' }
        ]}
      />

      <div style={styles.toolbar}>
        {isAdmin && (
          <button
            onClick={handleOpenCreate}
            style={styles.createButton}
            data-testid="create-field-button"
          >
            + Add Derived Field
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      <div style={styles.functionsPanel}>
        <strong>Allowed Functions:</strong> {allowedFunctions.join(', ')}
      </div>

      {loading ? (
        <div style={styles.loading}>Loading derived fields...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="derived-fields-table">
            <thead>
              <tr>
                <th style={styles.th}>Key</th>
                <th style={styles.th}>Dataset</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Expression</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(field => (
                <tr key={field.id} style={styles.tr} data-testid={`field-${field.id}`}>
                  <td style={styles.td}>
                    <code style={styles.keyCode}>{field.key}</code>
                  </td>
                  <td style={styles.td}>{field.dataset}</td>
                  <td style={styles.td}>
                    <span style={styles.typeBadge}>{field.fieldType}</span>
                  </td>
                  <td style={styles.td}>
                    <code style={styles.expressionCode}>{field.expression}</code>
                  </td>
                  <td style={styles.td}>{field.description || '-'}</td>
                  <td style={styles.td}>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => handleOpenEdit(field)}
                          style={styles.editButton}
                          data-testid={`edit-${field.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(field)}
                          style={styles.deleteButton}
                          data-testid={`delete-${field.id}`}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {fields.length === 0 && (
                <tr>
                  <td colSpan="6" style={styles.emptyState}>No derived fields defined</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="derived-field-modal">
            <h3 style={styles.modalTitle}>
              {editingField ? 'Edit Derived Field' : 'New Derived Field'}
            </h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Key (field name)</label>
              <input
                type="text"
                value={form.key}
                onChange={(e) => setForm(prev => ({ ...prev, key: e.target.value }))}
                style={styles.input}
                placeholder="e.g., avgEnergyPerSession"
                data-testid="field-key-input"
              />
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Dataset</label>
                <select
                  value={form.dataset}
                  onChange={(e) => setForm(prev => ({ ...prev, dataset: e.target.value }))}
                  style={styles.select}
                  data-testid="field-dataset-input"
                >
                  <option value="sessions">Sessions</option>
                  <option value="assets">Assets</option>
                  <option value="sites">Sites</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Type</label>
                <select
                  value={form.fieldType}
                  onChange={(e) => setForm(prev => ({ ...prev, fieldType: e.target.value }))}
                  style={styles.select}
                  data-testid="field-type-input"
                >
                  <option value="number">Number</option>
                  <option value="string">String</option>
                  <option value="boolean">Boolean</option>
                </select>
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Expression</label>
              <textarea
                value={form.expression}
                onChange={(e) => { setForm(prev => ({ ...prev, expression: e.target.value })); setValidationResult(null); }}
                style={styles.textarea}
                placeholder="e.g., DIVIDE(energyDelivered, sessionCount)"
                rows={3}
                data-testid="field-expression-input"
              />
              <button
                onClick={handleValidate}
                style={styles.validateButton}
                data-testid="validate-button"
              >
                Validate
              </button>
              {validationResult && (
                <div style={{
                  ...styles.validationResult,
                  background: validationResult.valid ? '#c6f6d5' : '#fed7d7',
                  color: validationResult.valid ? '#276749' : '#c53030'
                }}>
                  {validationResult.valid ? 'Expression is valid' : validationResult.errors.join(', ')}
                </div>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                style={styles.input}
                placeholder="Optional description"
                data-testid="field-description-input"
              />
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.key || !form.expression}
                style={styles.submitButton}
                data-testid="save-field-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' },
  createButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  functionsPanel: { padding: '0.75rem 1rem', background: '#edf2f7', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.75rem', color: '#4a5568' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.875rem', color: '#4a5568' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '1rem', fontSize: '0.875rem' },
  keyCode: { background: '#edf2f7', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontFamily: 'monospace' },
  typeBadge: { padding: '0.25rem 0.5rem', background: '#e2e8f0', borderRadius: '4px', fontSize: '0.75rem' },
  expressionCode: { background: '#fefcbf', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontFamily: 'monospace' },
  editButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer', marginRight: '0.5rem' },
  deleteButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #c53030', background: 'white', color: '#c53030', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '550px', maxWidth: '650px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  formGroup: { marginBottom: '1rem', flex: 1 },
  formRow: { display: 'flex', gap: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' },
  input: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  select: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white' },
  textarea: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', fontFamily: 'monospace', resize: 'vertical' },
  validateButton: { marginTop: '0.5rem', padding: '0.375rem 0.75rem', borderRadius: '4px', border: '1px solid #718096', background: 'white', color: '#718096', fontSize: '0.75rem', cursor: 'pointer' },
  validationResult: { marginTop: '0.5rem', padding: '0.5rem', borderRadius: '4px', fontSize: '0.75rem' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  submitButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
